// src/pages/sales/PageQuotations.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { downloadPdf } from "../../apiService";
import { fmtDate } from "../../utils/dateUtils";
import { QuotationCreateModal } from "./QuotationCreateModal";
import { QuotationDetailModal } from "./QuotationDetailModal";
import { QuotationEditModal }   from "./QuotationEditModal";
import { useToast } from "../../components/Toast";

const STATUS_STYLE = {
  DRAFT:    { bg: "#f3f4f6", color: "#374151" },
  SENT:     { bg: "#dbeafe", color: "#1e40af" },
  ACCEPTED: { bg: "#d1fae5", color: "#065f46" },
  REJECTED: { bg: "#fee2e2", color: "#991b1b" },
  EXPIRED:  { bg: "#fef3c7", color: "#92400e" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] ?? STATUS_STYLE.DRAFT;
  return (
    <span style={{ background: s.bg, color: s.color, borderRadius: 4,
      padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
      {status}
    </span>
  );
}

export function PageQuotations({ myPerms, fgs, sfgs, hmGrades, userName, userEmail = "" }) {
  const showToast = useToast();
  const [rows,       setRows]       = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow,  setDetailRow]  = useState(null);
  const [editRow,    setEditRow]    = useState(null);

  const [customerFilter, setCustomerFilter] = useState("");
  const [gradeFilter,    setGradeFilter]    = useState("");

  const canCreate  = myPerms?.includes("QUOTATION_CREATE");
  const canManage  = myPerms?.includes("CRM_MANAGE");

  const baseRows = canManage ? rows : rows.filter(r => r.createdBy === userEmail);

  const displayRows = useMemo(() => {
    const cust  = customerFilter.trim().toLowerCase();
    const grade = gradeFilter.trim().toLowerCase();
    return baseRows.filter(r => {
      if (cust  && !r.customerName?.toLowerCase().includes(cust)) return false;
      if (grade && !r.lines?.some(l =>
        l.productName?.toLowerCase().includes(grade) ||
        l.productCode?.toLowerCase().includes(grade))) return false;
      return true;
    });
  }, [baseRows, customerFilter, gradeFilter]);

  function load() {
    setLoading(true);
    api.fetchQuotations()
      .then(setRows)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function handleCreate(body) {
    const created = await api.createQuotation(body);
    setRows(old => [created, ...old]);
    showToast("Quotation created");
    setCreateOpen(false);
  }

  async function handleUpdate(id, body) {
    const updated = await api.updateQuotation(id, body);
    setRows(old => old.map(r => r.id === updated.id ? updated : r));
    setDetailRow(updated);
    showToast("Quotation updated");
    setEditRow(null);
    return updated;
  }

  async function handleStatusChange(id, status) {
    const updated = await api.updateQuotationStatus(id, status);
    setRows(old => old.map(r => r.id === updated.id ? updated : r));
    setDetailRow(updated);
    showToast("Status updated");
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Quotations</div>
          <div className="page-subtitle">
            {displayRows.length}{baseRows.length !== displayRows.length ? ` of ${baseRows.length}` : ""} quotation{displayRows.length !== 1 ? "s" : ""}
          </div>
        </div>
        <div className="page-header-right" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <input
            type="text"
            value={customerFilter}
            onChange={e => setCustomerFilter(e.target.value)}
            placeholder="Filter by customer…"
            className="btn"
            style={{ fontSize: 12, padding: "5px 10px", width: 180 }}
          />
          <input
            type="text"
            value={gradeFilter}
            onChange={e => setGradeFilter(e.target.value)}
            placeholder="Filter by grade…"
            className="btn"
            style={{ fontSize: 12, padding: "5px 10px", width: 160 }}
          />
          {canCreate && (
            <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>+ New Quotation</button>
          )}
        </div>
      </div>

      {loading && <div style={{ color: "#aaa", fontSize: 12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>REF No.</th>
                <th>Date</th>
                <th>Customer</th>
                <th>Company</th>
                <th style={{ textAlign: "right" }}>Lines</th>
                <th style={{ textAlign: "center" }}>Rev.</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {displayRows.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: "center", color: "#aaa", padding: 24 }}>
                  {customerFilter || gradeFilter ? "No quotations match your filters." : "No quotations yet"}
                </td></tr>
              ) : displayRows.map(row => (
                <tr key={row.id} style={{ cursor: "pointer" }} onClick={() => setDetailRow(row)}>
                  <td style={{ fontFamily: "monospace", fontWeight: 600 }}>{row.quoNumber}</td>
                  <td style={{ fontSize: 12 }}>{row.date ? fmtDate(row.date) : "—"}</td>
                  <td className="fw-600">{row.customerName}</td>
                  <td style={{ fontSize: 11 }}>
                    {row.quoNumber?.startsWith("AMIQ") ? "AMI — Industries" : "AMT — Trading"}
                  </td>
                  <td style={{ textAlign: "right", fontSize: 12 }}>{row.lines?.length ?? 0}</td>
                  <td style={{ textAlign: "center" }}>
                    {(row.revisionCount > 0) ? (
                      <span style={{ background: "rgba(68,0,153,0.1)", color: "#440099",
                        borderRadius: 10, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>
                        {row.revisionCount}
                      </span>
                    ) : <span style={{ color: "#ccc", fontSize: 11 }}>—</span>}
                  </td>
                  <td><StatusBadge status={row.status} /></td>
                  <td onClick={e => e.stopPropagation()}>
                    <button className="btn-small" onClick={() =>
                      downloadPdf(`/api/crm/quotations/${row.id}/pdf`,
                        row.quoNumber.replace(/\//g, "-") + ".pdf")
                    }>PDF</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setCreateOpen(false); }}>
          <QuotationCreateModal
            fgs={fgs}
            sfgs={sfgs}
            hmGrades={hmGrades}
            userName={userName}
            onSave={handleCreate}
            onCancel={() => setCreateOpen(false)}
          />
        </div>
      )}

      {detailRow && !editRow && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setDetailRow(null); }}>
          <QuotationDetailModal
            quotation={detailRow}
            canCreate={canCreate}
            onStatusChange={handleStatusChange}
            onEdit={row => setEditRow(row)}
            onClose={() => setDetailRow(null)}
          />
        </div>
      )}

      {editRow && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setEditRow(null); }}>
          <QuotationEditModal
            quotation={editRow}
            fgs={fgs}
            sfgs={sfgs}
            hmGrades={hmGrades}
            onSave={handleUpdate}
            onCancel={() => setEditRow(null)}
          />
        </div>
      )}
    </>
  );
}
